import React, { useEffect, useState, createElement } from 'react';
import { ArrowLeftIcon, ChevronRightIcon, SparklesIcon, BriefcaseIcon, ClipboardIcon, UserIcon, StethoscopeIcon, BarChart3Icon, MessageCircleIcon, UsersIcon, FolderIcon, WalletIcon, UtensilsIcon, BoxIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ROLES, CATEGORIES, SOLUTIONS, PainCategory } from '../data/painSolutions';
type Screen = 'role' | 'category' | 'pain';
const STORAGE_KEY = 'meelo-find-solution-state';
// ─── Icon registries (consistent line-icon style) ──────────────────────────
const ROLE_ICONS: Record<string, BoxIcon> = {
  owner: BriefcaseIcon,
  methodist: ClipboardIcon,
  teacher: UserIcon,
  nurse: StethoscopeIcon
};
const CATEGORY_ICONS: Record<string, BoxIcon> = {
  attendance: BarChart3Icon,
  parents: MessageCircleIcon,
  staff: UsersIcon,
  documents: FolderIcon,
  finance: WalletIcon,
  nutrition: UtensilsIcon
};
// ─── Page-level SEO ────────────────────────────────────────────────────────
function useFindSolutionSEO() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Найдите своё решение | Meelo';
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', 'Покажем, как Meelo решает именно вашу ситуацию');
    setMeta('property', 'og:title', 'Найдите своё решение | Meelo');
    setMeta('property', 'og:description', 'Покажем, как Meelo решает именно вашу ситуацию');
    return () => {
      document.title = prevTitle;
    };
  }, []);
}
// ─── Instant scroll-to-top helper ──────────────────────────────────────────
function scrollToTopInstant() {
  const html = document.documentElement;
  const prev = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  window.scrollTo({
    top: 0,
    behavior: 'instant' as ScrollBehavior
  });
  requestAnimationFrame(() => {
    html.style.scrollBehavior = prev;
  });
}
export function FindSolutionPage() {
  useFindSolutionSEO();
  const [screen, setScreen] = useState<Screen>('role');
  const [role, setRole] = useState<string | null>(null);
  const [category, setCategory] = useState<PainCategory | null>(null);
  // ── Ensure we land at the top on direct navigation ──
  useEffect(() => {
    scrollToTopInstant();
  }, []);
  // ── Restore prior state on mount (e.g., when returning from a pain page) ──
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        screen?: Screen;
        roleId?: string | null;
        categoryId?: string | null;
      };
      if (parsed.screen === 'pain' && parsed.categoryId) {
        const cat = CATEGORIES.find(c => c.id === parsed.categoryId);
        if (cat) {
          if (parsed.roleId) setRole(parsed.roleId);
          setCategory(cat);
          setScreen('pain');
        }
      } else if (parsed.screen === 'category' && parsed.roleId) {
        setRole(parsed.roleId);
        setScreen('category');
      }
    } catch {
      // ignore
    }
  }, []);
  // ── Persist state on change ──
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        screen,
        roleId: role,
        categoryId: category?.id ?? null
      }));
    } catch {
      // ignore
    }
  }, [screen, role, category]);
  const handleRolePick = (id: string, available: boolean) => {
    if (!available) return;
    setRole(id);
    setScreen('category');
    setTimeout(scrollToTopInstant, 0);
  };
  const handleCategoryPick = (cat: PainCategory) => {
    setCategory(cat);
    setScreen('pain');
    setTimeout(scrollToTopInstant, 0);
  };
  const handlePainPick = (painId: string) => {
    window.location.hash = `#/find-solution/pain/${painId}`;
    // The PainSolutionPage handles scrolling to top on mount
  };
  const goBack = () => {
    if (screen === 'pain') {
      setScreen('category');
      setCategory(null);
    } else if (screen === 'category') {
      setScreen('role');
      setRole(null);
    }
    setTimeout(scrollToTopInstant, 0);
  };
  const stepIndex = screen === 'role' ? 1 : screen === 'category' ? 2 : 3;
  return <div className="min-h-screen bg-[#FAFAFA] font-sans text-gray-900" style={{
    animation: 'fadeIn 200ms ease-out'
  }} data-id="element-299">
      <Navbar data-id="element-300" />
      <main data-id="element-301">
        <section id="find-solution" className="relative pt-28 pb-20 sm:pt-32 sm:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[calc(100vh-80px)]" style={{
        background: 'linear-gradient(180deg, #FAFAFA 0%, #F4EEFF 50%, #FAFAFA 100%)'
      }} data-id="element-302">
          {/* Decorative glow */}
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 30%, rgba(124,77,255,0.10) 0%, transparent 60%)'
        }} data-id="element-303" />

          <div className="relative max-w-5xl mx-auto" data-id="element-304">
            {/* Header */}
            <div className="text-center mb-8 sm:mb-10" data-id="element-305">
              <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-[#7C4DFF] bg-white border border-[#E9DDFF] px-2.5 py-1 rounded-full mb-4 shadow-sm" data-id="element-306">
                <SparklesIcon size={11} strokeWidth={2.2} data-id="element-307" />
                Подбор решения
              </span>
              <h1 className="text-[28px] sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-[1.15] tracking-tight" data-id="element-308">
                Найдите своё решение
              </h1>
              <p className="text-sm sm:text-base text-gray-500 mt-3 max-w-xl mx-auto" data-id="element-309">
                Покажем, как Meelo решает именно вашу ситуацию
              </p>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-3xl shadow-[0_8px_40px_-12px_rgba(124,77,255,0.18)] border border-gray-100 overflow-hidden" data-id="element-310">
              {/* Step indicator */}
              <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-2" data-id="element-311">
                <button onClick={goBack} disabled={screen === 'role'} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C4DFF] hover:text-[#651FFF] transition-colors disabled:opacity-0 disabled:pointer-events-none" data-id="element-312">
                  <ArrowLeftIcon size={15} strokeWidth={2.2} data-id="element-313" />
                  Назад
                </button>

                <div className="flex items-center gap-2" data-id="element-314">
                  {[1, 2, 3].map(n => <div key={n} className="h-1.5 rounded-full transition-all duration-300" style={{
                  width: n === stepIndex ? 28 : 14,
                  background: n <= stepIndex ? '#7C4DFF' : '#E9DDFF'
                }} data-id="element-315" />)}
                </div>

                {/* Spacer to keep progress dots centered */}
                <div className="w-[60px]" aria-hidden="true" data-id="element-316" />
              </div>

              {/* Screen content */}
              <div key={screen} className="px-6 sm:px-8 lg:px-12 pb-8 sm:pb-12 pt-4" style={{
              animation: 'fadeInUp 320ms ease both'
            }} data-id="element-317">
                {screen === 'role' && <RoleScreen onPick={handleRolePick} data-id="element-318" />}

                {screen === 'category' && <CategoryScreen onPick={handleCategoryPick} data-id="element-319" />}

                {screen === 'pain' && category && <PainScreen category={category} onPick={handlePainPick} data-id="element-320" />}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-321" />
    </div>;
}
// ─── Screen 1: Role ────────────────────────────────────────────────────────
function RoleScreen({
  onPick
}: {
  onPick: (id: string, available: boolean) => void;
}) {
  return <div data-id="element-322">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center" data-id="element-323">
        Кто вы?
      </h2>
      <p className="text-sm text-gray-500 mb-8 text-center" data-id="element-324">
        Покажем, что именно решает Meelo
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-325">
        {ROLES.map(role => {
        const Icon = ROLE_ICONS[role.id] ?? UserIcon;
        return <button key={role.id} onClick={() => onPick(role.id, role.available)} disabled={!role.available} className="group relative flex flex-col items-center justify-center text-center rounded-2xl border bg-white px-3 py-6 sm:py-7 transition-all duration-200 disabled:cursor-not-allowed" style={{
          borderColor: role.available ? '#E9DDFF' : '#F0F0F0',
          boxShadow: role.available ? '0 2px 8px -2px rgba(124,77,255,0.08)' : 'none'
        }} data-id="element-326">
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{
            background: 'linear-gradient(135deg, rgba(124,77,255,0.06) 0%, rgba(124,77,255,0.02) 100%)',
            display: role.available ? 'block' : 'none'
          }} data-id="element-327" />
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-3 transition-transform group-hover:scale-105" style={{
            background: role.available ? 'linear-gradient(135deg, #F5F0FF 0%, #EDE7F6 100%)' : '#F7F7F7'
          }} data-id="element-328">
                <Icon size={26} strokeWidth={1.6} className={role.available ? 'text-[#7C4DFF]' : 'text-gray-400'} data-id="element-329" />
              </div>
              <p className="relative text-sm font-semibold leading-snug" style={{
            color: role.available ? '#1A1A2E' : '#9CA3AF'
          }} data-id="element-330">
                {role.label}
              </p>
              {!role.available && <span className="relative mt-2 text-[10px] font-bold tracking-wider uppercase text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-331">
                  Скоро
                </span>}
              {role.available && <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#7C4DFF] animate-pulse" data-id="element-332" />}
            </button>;
      })}
      </div>
    </div>;
}
// ─── Screen 2: Category ────────────────────────────────────────────────────
function CategoryScreen({
  onPick
}: {
  onPick: (cat: PainCategory) => void;
}) {
  return <div data-id="element-333">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-334">
        Что хочется улучшить?
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-id="element-335">
        {CATEGORIES.map(cat => {
        const Icon = CATEGORY_ICONS[cat.id] ?? FolderIcon;
        return <button key={cat.id} onClick={() => onPick(cat)} className="group relative text-left rounded-2xl bg-white border px-4 sm:px-5 py-5 sm:py-6 transition-all duration-200 hover:-translate-y-0.5" style={{
          borderColor: cat.highlighted ? '#7C4DFF' : '#E9DDFF',
          boxShadow: cat.highlighted ? '0 8px 24px -8px rgba(124,77,255,0.30), inset 0 0 0 1px rgba(124,77,255,0.10)' : '0 2px 8px -2px rgba(124,77,255,0.08)'
        }} data-id="element-336">
              {cat.highlighted && <span className="absolute -top-2.5 left-4 text-[10px] font-bold tracking-wider uppercase text-white bg-[#7C4DFF] px-2 py-0.5 rounded-full shadow-md" data-id="element-337">
                  Чаще всего
                </span>}

              <div className="flex items-start gap-3" data-id="element-338">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, #F5F0FF 0%, #EDE7F6 100%)'
            }} data-id="element-339">
                  <Icon size={22} strokeWidth={1.6} className="text-[#7C4DFF]" data-id="element-340" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-341">
                  <p className="text-[15px] font-semibold text-gray-900 leading-snug" data-id="element-342">
                    {cat.title}
                  </p>
                  <p className="text-xs font-medium text-gray-600 mt-1.5" data-id="element-343">
                    {cat.pains.length}{' '}
                    {cat.pains.length === 1 ? 'сценарий' : cat.pains.length < 5 ? 'сценария' : 'сценариев'}
                  </p>
                </div>
                <ChevronRightIcon size={18} className="flex-shrink-0 text-gray-300 group-hover:text-[#7C4DFF] group-hover:translate-x-0.5 transition-all mt-1" strokeWidth={2.2} data-id="element-344" />
              </div>
            </button>;
      })}
      </div>
    </div>;
}
// ─── Screen 3: Pain ────────────────────────────────────────────────────────
function PainScreen({
  category,
  onPick
}: {
  category: PainCategory;
  onPick: (painId: string) => void;
}) {
  const Icon = CATEGORY_ICONS[category.id] ?? FolderIcon;
  return <div data-id="element-345">
      {/* Category badge */}
      <div className="flex items-center justify-center gap-2 mb-4" data-id="element-346">
        <Icon size={16} strokeWidth={1.8} className="text-[#7C4DFF]" data-id="element-347" />
        <span className="text-xs font-bold tracking-widest uppercase text-[#7C4DFF]" data-id="element-348">
          {category.title}
        </span>
      </div>

      <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 text-center max-w-2xl mx-auto leading-[1.2]" data-id="element-349">
        {category.headline}
      </h2>
      <p className="text-sm text-gray-500 mb-8 text-center" data-id="element-350">
        {category.subheadline}
      </p>

      <div className="flex flex-col gap-2.5 max-w-2xl mx-auto" data-id="element-351">
        {category.pains.map(pain => {
        const hasSolution = !!SOLUTIONS[pain.id];
        return <button key={pain.id} onClick={() => onPick(pain.id)} className="group flex items-center gap-3 text-left rounded-xl bg-white border border-[#EDE7F6] px-4 py-3.5 sm:px-5 sm:py-4 transition-all duration-200 hover:border-[#7C4DFF] hover:-translate-y-0.5" style={{
          boxShadow: '0 2px 6px -2px rgba(124,77,255,0.06)'
        }} data-id="element-352">
              <p className="flex-1 text-[14px] sm:text-[15px] text-gray-800 font-medium leading-relaxed" data-id="element-353">
                {pain.text}
              </p>
              <ChevronRightIcon size={16} className="flex-shrink-0 text-gray-300 group-hover:text-[#7C4DFF] group-hover:translate-x-0.5 transition-all" strokeWidth={2.2} data-id="element-354" />
            </button>;
      })}
      </div>
    </div>;
}